import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../lib/frontend-db';

const DynamicFavicon = () => {
  const [faviconUrl, setFaviconUrl] = useState<string>('');

  useEffect(() => {
    loadFavicon();
  }, []);

  const loadFavicon = async () => {
    try {
      const settings = await db.getSettings() as any;
      if (settings && settings.header_logo_url) {
        const logoUrl = settings.header_logo_url;
        setFaviconUrl(logoUrl);
        updateFavicon(logoUrl);
        console.log('✅ Favicon loaded from settings:', logoUrl);
      } else {
        console.log('ℹ️ No header logo found in settings');
      }
    } catch (error) {
      console.error('❌ Error loading favicon:', error);
    }
  };

  const updateFavicon = (url: string) => {
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll('link[rel*="icon"], link[rel*="shortcut"], link[rel*="apple-touch-icon"]');
    existingLinks.forEach(link => link.remove());

    // Add cache-busting parameter to force browser refresh
    const cacheBuster = `?v=${Date.now()}`;
    const faviconUrl = url + cacheBuster;

    // Create multiple favicon sizes for better browser compatibility
    const sizes = ['16x16', '32x32', '48x48', '64x64', '96x96', '128x128', '192x192', '256x256'];
    
    sizes.forEach(size => {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      link.sizes = size;
      link.href = faviconUrl;
      document.head.appendChild(link);
    });

    // Main favicon without size (fallback)
    const mainLink = document.createElement('link');
    mainLink.rel = 'icon';
    mainLink.type = 'image/png';
    mainLink.href = faviconUrl;
    document.head.appendChild(mainLink);

    // Shortcut icon (legacy support)
    const shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    shortcutLink.type = 'image/png';
    shortcutLink.href = faviconUrl;
    document.head.appendChild(shortcutLink);

    // Apple touch icon for iOS
    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = faviconUrl;
    document.head.appendChild(appleLink);

    // Apple touch icon with sizes
    const appleLinkPrecomposed = document.createElement('link');
    appleLinkPrecomposed.rel = 'apple-touch-icon-precomposed';
    appleLinkPrecomposed.href = faviconUrl;
    document.head.appendChild(appleLinkPrecomposed);

    console.log('🎯 Favicon updated:', faviconUrl);
  };

  // Listen for settings changes to update favicon dynamically
  useEffect(() => {
    const handleSettingsChange = () => {
      console.log('🔄 Settings updated, reloading favicon...');
      setTimeout(() => {
        loadFavicon();
      }, 100); // Small delay to ensure settings are saved
    };

    // Listen for custom event when settings are updated
    window.addEventListener('settingsUpdated', handleSettingsChange);
    
    // Also listen for focus events to refresh favicon when user returns to tab
    const handleFocus = () => {
      if (faviconUrl) {
        updateFavicon(faviconUrl);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [faviconUrl]);

  // Add a global function to manually refresh favicon (for debugging)
  useEffect(() => {
    (window as any).refreshFavicon = () => {
      console.log('🔄 Manual favicon refresh triggered');
      loadFavicon();
    };
  }, []);

  // This component doesn't render anything visible
  return null;
};

export default DynamicFavicon;
